import React, { useCallback, useState } from "react";
import styles from "../../shared/ui/ModalSaveModeSelectedModal/ModalSaveModeSelectedModal.module.scss";
import { useCurrentOrganization } from "@/Hooks";
import { useDownloadImagesMutation } from "@/features/auth/api/authAPI";
import { toast } from "react-toastify";
import useAuthProtection from "@/Hooks/useAuthProtection";
import { saveModeActions } from "@/entities/saveMode";
import { useDispatch } from "react-redux";
import Spinner from "@/shared/ui/Spinner/Spinner";
import { useOpenProModal } from "@/Hooks/useOpenBillingModal";

interface SavedModeSelectPopupProps {
  handleDeselectAll: () => void;
  handleRemoveFromGroup: () => Promise<void>;
  totalSelected: number;
  selectedCurrentGroups: Set<{
    id: number;
    url: string;
    videoId: number;
    type: string;
    screenId?: number;
  }>;
  groupName: string;
}

const SavedModeSelectPopup: React.FC<SavedModeSelectPopupProps> = ({
  handleDeselectAll,
  selectedCurrentGroups,
  handleRemoveFromGroup,
  totalSelected,
  groupName,
}) => {
  const currentOrganization = useCurrentOrganization();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadImages] = useDownloadImagesMutation();
  const dispatch = useDispatch();
  const { withAuth } = useAuthProtection();

  const handleDownload = useCallback(async () => {
    // Create empty arrays for all screen types
    const savedPoints: number[] = [];
    const savedObScreens: number[] = [];
    const appVideoAnimations: number[] = [];
    const storeScreens: number[] = [];

    // Categorize screen IDs based on their type
    Array.from(selectedCurrentGroups).forEach((item) => {
      if (item.screenId) {
        switch (item.type) {
          case "video":
            savedPoints.push(item.screenId);
            break;
          case "store":
            storeScreens.push(item.screenId);
            break;
          case "onboarding":
            savedObScreens.push(item.screenId);
            break;
          case "animation":
            appVideoAnimations.push(item.screenId);
            break;
        }
      }
    });

    // Get the organization ID
    const organizationId = currentOrganization?.uuid;

    if (!organizationId) {
      toast.error("Organization ID not found");
      return;
    }

    // Prepare the payload with only non-empty arrays
    const payload: any = {
      organizationId: organizationId,
    };

    // Only include non-empty arrays in the payload
    if (savedPoints.length > 0) {
      payload.saved_points = savedPoints;
    }
    if (savedObScreens.length > 0) {
      payload.saved_ob_screens = savedObScreens;
    }
    if (appVideoAnimations.length > 0) {
      payload.app_video_animations = appVideoAnimations;
    }
    if (storeScreens.length > 0) {
      payload.store_screens = storeScreens;
    }

    setIsDownloading(true);

    try {
      await toast
        .promise(downloadImages(payload).unwrap(), {
          pending: "Preparing your download...",
          success: "Screens downloaded successfully",
          error: "Failed to download screens",
        })
        .then((blob) => {
          // Create a URL for the blob
          const url = window.URL.createObjectURL(blob);

          // Create a temporary link element
          const link = document.createElement("a");
          link.href = url;
          link.download = `${groupName}_screens.zip`;

          // Trigger the download
          document.body.appendChild(link);
          link.click();

          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        });
    } catch (error) {
      console.error("Failed to download screens:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [selectedCurrentGroups, downloadImages, currentOrganization]);

  const openProModal = useOpenProModal();

  const handleDownloadClick = withAuth((e) => {
    e.stopPropagation();
    if (!currentOrganization?.is_pro) {
      // If user is authenticated but not Pro, show Pro modal
      openProModal("Saved Mode Select Popup");
    } else {
      // If user is authenticated and Pro, proceed with download
      handleDownload();
    }
  });

  return (
    <>
      <div className={styles.root}>
        <div onClick={handleDeselectAll} className={styles.selectedItem}>
          <p>{totalSelected} Selected</p>
          <div className={styles.imageCloseModalSaveContainer}>
            <img src="/img/whiteIcon/Close.svg" alt="Deselect All" />
          </div>
        </div>
        <div className={styles.selectedContainerRight}>
          <div onClick={handleRemoveFromGroup} className={styles.groupSave}>
            <p>Remove from Group</p>
          </div>
          <button
            className={styles.selectedScreensPopupDownloadButton}
            onClick={handleDownloadClick}
            disabled={isDownloading}
          >
            <img src="/img/blackIcon/Download.svg" alt="Download Button Icon" />
            <p>{isDownloading ? "Downloading..." : "Download"}</p>
            {!currentOrganization?.is_pro && (
              <div className={styles.selectedScreensPopupDownloadButtonChip}>
                <p>Pro</p>
              </div>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default SavedModeSelectPopup;
